import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useCallback, useMemo } from "react";

import styled from "styled-components/macro";

import { faMobileAlt } from "@fortawesome/pro-solid-svg-icons";
import { motion } from "framer-motion";

import FnrNetworkPhoto from "UI/FnrNetworkPhoto";
import { colors } from "UI/colors";

import { formatRelativeDate } from "Utilities/Date/formatRelativeDate";

import NoteProxyInfo from "./components/NoteProxyInfo";

const NoteCard = ({ note, isMobile, currentFontFamily, onNoteClick }) => {
  const formattedDate = useMemo(() => {
    if (note.publishedDate === 0) return null;
    return formatRelativeDate(
      new Date(note.publishedDate * 1000).toISOString()
    );
  }, [note.publishedDate]);

  const previewText = useMemo(() => {
    const stringLimit = isMobile ? 200 : 350;
    if (!note.previewText) return "";
    if (note.previewText.length > 200) {
      return `${note.previewText.substring(0, stringLimit)}...`;
    }
    return note.previewText;
  }, [note.previewText, isMobile]);

  const handleClick = useCallback(() => {
    onNoteClick(note);
  }, [note, onNoteClick]);

  return (
    <NoteCardContainer
      key={note.ID}
      onClick={handleClick}
      $isMobile={isMobile}
      as={motion.div}
      whileHover={{
        opacity: 0.95,
        boxShadow:
          "0 12px 40px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(255, 255, 255, 0.15)",
      }}
      transition={{
        duration: 0.2,
        ease: "easeOut",
      }}
    >
      <LeftContent>
        <NoteDetails>
          <NoteHeader>
            <NoteCardGroupImage>
              <FnrNetworkPhoto
                size={isMobile ? "sm" : "lg"}
                src={note.groupIcon}
              />
            </NoteCardGroupImage>
            <NoteHeaderContent>
              <GroupName>{note.from || ""}</GroupName>
            </NoteHeaderContent>
          </NoteHeader>
          <NoteTitle>
            {note.sms && (
              <FontAwesomeIcon
                color={colors.fnLtGray}
                icon={faMobileAlt}
                size="lg"
                style={{ marginRight: "5px", marginTop: "5px" }}
              />
            )}
            <NoteTitleText $fontFamily={currentFontFamily} $isMobile={isMobile}>
              {note.title || ""}
            </NoteTitleText>
          </NoteTitle>
          <NoteSummary $isMobile={isMobile}>
            <NotePreviewText
              $fontFamily={currentFontFamily}
              $isMobile={isMobile}
            >
              {!note.sms ? previewText : ""}
            </NotePreviewText>
          </NoteSummary>
        </NoteDetails>

        <FooterSection $isMobile={isMobile}>
          <span>Sent by {note.author}</span>
          <span>•</span>
          {formattedDate && <NoteDate>{formattedDate}</NoteDate>}
          <NoteProxyInfo receivedViaProxyUsers={note.receivedViaProxyUsers} />
        </FooterSection>
      </LeftContent>
      {note.thumbnailUrl && (
        <RightContent>
          <MediaThumbnail $isMobile={isMobile} src={note?.thumbnailUrl} />
        </RightContent>
      )}
    </NoteCardContainer>
  );
};

const MediaThumbnail = styled.img`
  width: 175px;
  height: 200px;
  border-radius: 8px;
  flex-shrink: 0;
  object-fit: cover;
  margin-left: 20px;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 0px 4px;
  transition: transform 0.3s ease;
  ${({ $isMobile }) =>
    $isMobile &&
    `
    display: none;
  `}

  @media (max-width: 800px) {
    width: 100px;
    height: 125px;
  }
`;

const NoteCardContainer = styled.div`
  background: linear-gradient(
    140deg,
    rgba(0, 0, 0, 0.35) 0%,
    rgba(0, 0, 0, 0.55) 100%
  );
  padding: ${({ $isMobile }) => ($isMobile ? "15px" : "30px")};
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  transition: border 0.3s;
  border-radius: 24px;
  cursor: pointer;
  color: #ffffff;

  &:hover {
    ${MediaThumbnail} {
      transform: scale(1.03);
    }
  }

  &:active {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
`;

const NoteCardGroupImage = styled.div`
  margin-right: 9px;
  & img {
    width: 30px;
    height: 30px;
    border: 2px solid white;
    box-shadow: 1px 1px 3px 0px rgb(0 0 0 / 36%);
  }
`;

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const NoteDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const NoteHeader = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
  align-items: center;
  font-size: 16px;
`;

const NoteHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const GroupName = styled.span`
  color: #ffffff;
  font-family: "Open Sans", sans-serif;
  font-size: 18px;
`;

const NoteDate = styled.span``;

const NoteTitle = styled.div`
  display: flex;
  margin-bottom: 10px;
  max-width: 100%;
  width: 100%;
`;

const NoteSummary = styled.div`
  color: #ffffff;
  line-height: 20px;
  font-size: 0.95rem;
  margin-bottom: 15px;

  p {
    margin: 0 0 10px 0;
  }
`;

const FooterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  margin-top: auto;
  color: #dcdcdc;
`;

const RightContent = styled.div`
  margin: auto 0;
`;

const NoteTitleText = styled.div`
  color: #ffffff;
  font-family: "Open Sans", sans-serif;
  font-weight: 400;
  font-size: 26px;
  line-height: 1.15;
`;

const NotePreviewText = styled.div`
  color: #ffffff;
  font-family: "Open Sans", sans-serif;
  font-weight: 300;
  font-size: 17px;
  line-height: 20px;

  p {
    margin: 0 0 10px 0;
  }
`;

NoteCard.propTypes = {
  note: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
  requireLogin: PropTypes.bool,
  triggerLogin: PropTypes.func,
  currentFontFamily: PropTypes.string,
  onNoteClick: PropTypes.func,
};

export default React.memo(NoteCard);
